<template>
    <div class="block-editor create-task">
        <div class="wrapper">
            <!-- Workflow Seçimi -->
            <section>
                <div class="title">{{ $t("message.workflow") }}</div>
                <CustomPicker
                    v-model="clone.workflowId"
                    :items="workflows"
                    idKey="_id"
                    labelKey="title"
                    :placeholder="$t('message.selectWorkflow')"
                />
            </section>

            <!-- Pipeline Stage Seçimi -->
            <section>
                <div class="title">{{ $t("message.pipelineStage") }}</div>
                <CustomPicker
                    v-model="clone.pipelineStageId"
                    :items="filteredPipelineStages"
                    idKey="_id"
                    labelKey="name"
                    :placeholder="$t('message.selectPipelineStage')"
                />
            </section>

            <!-- Başlık -->
            <section>
                <div class="title">{{ $t("message.title") }}</div>
                <TiptapEditor
                    :placeholder="$t('message.taskTitle')"
                    :show-attributes="true"
                    v-model="clone.title"
                    automation-mode
                />
            </section>

            <!-- Açıklama -->
            <section>
                <div class="title">{{ $t("message.description") }}</div>
                <TiptapEditor
                    :placeholder="$t('message.taskDescription')"
                    :show-attributes="true"
                    v-model="clone.description"
                    automation-mode
                />
            </section>

            <!-- Öncelik -->
            <section>
                <div class="title">{{ $t("message.priority") }}</div>
                <CustomPicker
                    v-model="clone.priority"
                    :items="priorityOptions"
                    idKey="value"
                    labelKey="label"
                    :placeholder="$t('message.selectPriority')"
                />
            </section>

            <!-- Atananlar -->
            <section>
                <div class="title">{{ $t("message.assignees") }}</div>
                <div v-for="(assignee, index) in clone.assigneeIds" :key="index" class="dynamic-field">
                    <div class="field-header">
                        <span>{{ $t("message.assignee") }} {{ index + 1 }}</span>
                        <div class="mode-switch">
                            <span class="switch-label">{{ $t("message.dynamic") }}</span>
                            <a-switch
                                size="small"
                                :checked="assignee.mode === 'dynamic'"
                                @change="(checked) => onModeChange(checked, assignee)"
                            />
                        </div>
                        <SupsisButton @click="removeAssignee(index)" icon danger xsmall>
                            <i class="material-icons">delete</i>
                        </SupsisButton>
                    </div>
                    <template v-if="assignee.mode === 'static'">
                        <CustomPicker
                            v-model="assignee.value"
                            :items="userOptions"
                            idKey="_id"
                            labelKey="name"
                            :placeholder="$t('message.selectUser')"
                        />
                    </template>
                    <template v-else>
                        <TiptapEditor
                            v-model="assignee.value"
                            :placeholder="$t('message.dynamicValue')"
                            :show-attributes="true"
                            automation-mode
                        />
                    </template>
                </div>
                <a-button type="dashed" @click="addAssignee" block>
                    <a-icon type="plus" />
                    {{ $t("message.addAssignee") }}
                </a-button>
            </section>

            <!-- Takipçiler -->
            <section>
                <div class="title">{{ $t("message.followers") }}</div>
                <div v-for="(follower, index) in clone.followers" :key="index" class="dynamic-field">
                    <div class="field-header">
                        <span>{{ $t("message.follower") }} {{ index + 1 }}</span>
                        <div class="mode-switch">
                            <span class="switch-label">{{ $t("message.dynamic") }}</span>
                            <a-switch
                                size="small"
                                :checked="follower.mode === 'dynamic'"
                                @change="(checked) => onFollowerModeChange(checked, follower)"
                            />
                        </div>
                        <SupsisButton @click="removeFollower(index)" icon danger xsmall>
                            <i class="material-icons">delete</i>
                        </SupsisButton>
                    </div>
                    <template v-if="follower.mode === 'static'">
                        <CustomPicker
                            v-model="follower.value"
                            :items="userOptions"
                            idKey="_id"
                            labelKey="name"
                            :placeholder="$t('message.selectFollower')"
                        />
                    </template>
                    <template v-else>
                        <TiptapEditor
                            v-model="follower.value"
                            :placeholder="$t('message.dynamicValue')"
                            :show-attributes="true"
                            automation-mode
                        />
                    </template>
                </div>
                <a-button type="dashed" @click="addFollower" block>
                    <a-icon type="plus" />
                    {{ $t("message.addFollower") }}
                </a-button>
            </section>

            <!-- İşbirlikçiler -->
            <section>
                <div class="title">{{ $t("message.collaborators") }}</div>
                <div v-for="(collaborator, index) in clone.collaborators" :key="index" class="dynamic-field">
                    <div class="field-header">
                        <span>{{ $t("message.collaborator") }} {{ index + 1 }}</span>
                        <div class="mode-switch">
                            <span class="switch-label">{{ $t("message.dynamic") }}</span>
                            <a-switch
                                size="small"
                                :checked="collaborator.mode === 'dynamic'"
                                @change="(checked) => onCollaboratorModeChange(checked, collaborator)"
                            />
                        </div>
                        <SupsisButton @click="removeCollaborator(index)" icon danger xsmall>
                            <i class="material-icons">delete</i>
                        </SupsisButton>
                    </div>
                    <template v-if="collaborator.mode === 'static'">
                        <CustomPicker
                            v-model="collaborator.value"
                            :items="userOptions"
                            idKey="_id"
                            labelKey="name"
                            :placeholder="$t('message.selectCollaborator')"
                        />
                    </template>
                    <template v-else>
                        <TiptapEditor
                            v-model="collaborator.value"
                            :placeholder="$t('message.dynamicValue')"
                            :show-attributes="true"
                            automation-mode
                        />
                    </template>
                </div>
                <a-button type="dashed" @click="addCollaborator" block>
                    <a-icon type="plus" />
                    {{ $t("message.addCollaborator") }}
                </a-button>
            </section>

            <!-- Sahip -->
            <section>
                <div class="title">{{ $t("message.owner") }}</div>
                <div class="dynamic-field">
                    <div class="field-header">
                        <div class="mode-switch">
                            <span class="switch-label">{{ $t("message.dynamic") }}</span>
                            <a-switch
                                size="small"
                                :checked="clone.ownerId.mode === 'dynamic'"
                                @change="(checked) => onOwnerModeChange(checked)"
                            />
                        </div>
                    </div>
                    <template v-if="clone.ownerId.mode === 'static'">
                        <CustomPicker
                            v-model="clone.ownerId.value"
                            :items="userOptions"
                            idKey="_id"
                            labelKey="name"
                            :placeholder="$t('message.selectOwner')"
                        />
                    </template>
                    <template v-else>
                        <TiptapEditor
                            v-model="clone.ownerId.value"
                            :placeholder="$t('message.enterOwnerId')"
                            :show-attributes="true"
                            automation-mode
                        />
                    </template>
                </div>
            </section>

            <!-- Etiketler -->
            <section>
                <div class="title">{{ $t("message.tags") }}</div>
                <div class="add-tags">
                    <div
                        style="
                            margin-top: 10px;
                            color: #ec4b1d;
                            font-weight: bold;
                            cursor: pointer;
                            display: flex;
                            align-items: center;
                        "
                    >
                        {{ $t("message.addTag") }}
                        <a-dropdown>
                            <a-button style="margin-left: 10px; padding: 2px">
                                <span class="material-icons">add</span>
                            </a-button>
                            <a-menu slot="overlay">
                                <template v-if="tags && tags.length > 0">
                                    <a-menu-item v-for="tag in tags" :key="tag._id" @click="selectTag(tag)" style="display: flex; align-items: center;">
                                        <div
                                            class="showColor"
                                            :style="{
                                                backgroundColor: tag.color || '#cccccc',
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '5px',
                                                marginRight: '8px'
                                            }">
                                        </div>
                                        <span>{{ tag.name }}</span>
                                    </a-menu-item>
                                </template>
                                <a-menu-item v-else disabled>
                                    <span>Etiket bulunamadı</span>
                                </a-menu-item>
                            </a-menu>
                        </a-dropdown>
                    </div>
                    <div v-if="clone.tags && clone.tags.length > 0" class="selected-tags">
                        <div v-for="tag in clone.tags" :key="tag._id" class="tag-item" :style="getTagStyle(tag.color)">
                            <span>{{ tag.name }}</span>
                            <span class="material-icons close-icon" @click="removeTag(tag._id)">close</span>
                        </div>
                    </div>
                </div>
            </section>

            <!-- İletişim ID -->
            <section>
                <div class="title">{{ $t("message.contactId") }}</div>
                <TiptapEditor
                    :placeholder="$t('message.contactIdPlaceholder')"
                    :show-attributes="true"
                    v-model="clone.contactId"
                    automation-mode
                />
            </section>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import { mapState, mapActions } from "vuex";
import TiptapEditor from "../../../../Chatbot/pages/dashboard/common/TiptapEditor.vue";
import CustomPicker from "../../../../../../components/pickers/CustomPicker.vue";
import SupsisButton from "../../../../Settings/components/SupsisButton.vue";

export default {
    name: "CreateTask",
    components: {
        TiptapEditor,
        CustomPicker,
        SupsisButton
    },
    props: ["value"],
    data() {
        return {
            clone: _.cloneDeep(this.value || {
                workflowId: null,
                pipelineStageId: null,
                title: "",
                description: "",
                priority: "low",
                assigneeIds: [],
                followers: [],
                collaborators: [],
                ownerId: { mode: "static", value: null },
                contactId: null,
                tags: []
            }),

            priorityOptions: [
                { value: "lowest", label: "En Düşük" },
                { value: "low", label: "Düşük" },
                { value: "medium", label: "Orta" },
                { value: "high", label: "Yüksek" },
                { value: "highest", label: "En Yüksek" }
            ],
            tags: []
        };
    },
    computed: {
        ...mapState({
            workflows: state => state.tasks.workflows,
            users: state => state.user.users.data
        }),
        userOptions() {
            return this.users.map(user => ({
                _id: user._id,
                name: `${user.name} (${user.email})`
            }));
        },
        filteredPipelineStages() {
            if (!this.clone.workflowId) return [];
            const workflow = this.workflows.find(w => w._id === this.clone.workflowId);
            return workflow ? workflow.pipeline : [];
        }
    },
    methods: {
        ...mapActions({
            getTags: "tag/getTags"
        }),
        addAssignee() {
            this.clone.assigneeIds.push({
                mode: "static",
                value: null
            });
        },
        removeAssignee(index) {
            this.clone.assigneeIds.splice(index, 1);
        },
        onModeChange(checked, item) {
            item.mode = checked ? "dynamic" : "static";
            item.value = null;
        },
        onOwnerModeChange(checked) {
            this.clone.ownerId.mode = checked ? "dynamic" : "static";
            this.clone.ownerId.value = null;
        },
        addFollower() {
            this.clone.followers.push({
                mode: "static",
                value: null
            });
        },
        removeFollower(index) {
            this.clone.followers.splice(index, 1);
        },
        onFollowerModeChange(checked, follower) {
            follower.mode = checked ? "dynamic" : "static";
            follower.value = null;
        },
        addCollaborator() {
            this.clone.collaborators.push({
                mode: "static",
                value: null
            });
        },
        removeCollaborator(index) {
            this.clone.collaborators.splice(index, 1);
        },
        onCollaboratorModeChange(checked, collaborator) {
            collaborator.mode = checked ? "dynamic" : "static";
            collaborator.value = null;
        },
        selectTag(tag) {
            if (!this.clone.tags) {
                this.$set(this.clone, 'tags', []);
            }
            
            const isExists = this.clone.tags.find((selectedTag) => selectedTag._id === tag._id);
            if (isExists) {
                this.clone.tags = this.clone.tags.filter((selectedTag) => selectedTag._id !== tag._id);
            } else {
                this.clone.tags = _.uniq([...this.clone.tags, tag]);
            }
        },
        removeTag(tagId) {
            if (!this.clone.tags) return;
            this.clone.tags = this.clone.tags.filter((tag) => tag._id !== tagId);
        },
        getTagStyle(color) {
            if (!color) return { backgroundColor: '#cccccc', color: '#000' };
            
            const isLight = this.isLightColor(color);
            return {
                backgroundColor: color,
                color: isLight ? "#000" : "#fff",
            };
        },
        isLightColor(color) {
            if (!color || typeof color !== 'string' || !color.startsWith('#') || color.length !== 7) {
                return true;
            }
            try {
                const r = parseInt(color.slice(1, 3), 16);
                const g = parseInt(color.slice(3, 5), 16);
                const b = parseInt(color.slice(5, 7), 16);
                const brightness = (r * 299 + g * 587 + b * 114) / 1000;
                return brightness > 155;
            } catch(e) {
                console.error("Renk hesaplama hatası:", e);
                return true;
            }
        },
        async getTaskTags() {
            try {
                const tags = await this.getTags("task");
                this.tags = Array.isArray(tags) ? tags : [];
            } catch (error) {
                console.error("Etiketleri getirirken hata oluştu:", error);
                this.tags = [];
            }
        }
    },
    mounted() {
        // Clone nesnesinin gerekli özelliklere sahip olduğunu garanti altına alalım
        if (!this.clone.tags) {
            this.$set(this.clone, 'tags', []);
        }
        
        // Etiketleri yükleyelim
        this.getTaskTags().catch(err => {
            console.error("Mounted hook içinde etiket yükleme hatası:", err);
        });
    },
    watch: {
        clone: {
            deep: true,
            handler(newValue) {
                this.$emit("input", newValue);
            }
        }
    }
};
</script>

<style lang="scss">
.block-editor.create-task {
    .wrapper {
        padding: 30px;

        section {
            margin-bottom: 20px;

            .title {
                margin-bottom: 10px;
                font-weight: 600;
                font-size: 16px;
                line-height: 20px;
            }
        }

        .dynamic-field {
            margin-bottom: 10px;
            padding: 10px;
            border: 1px solid #e8e8e8;
            border-radius: 4px;

            .field-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 10px;

                .mode-switch {
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    .switch-label {
                        font-size: 12px;
                        color: rgba(0, 0, 0, 0.65);
                    }
                }
            }
        }
        
        .add-tags {
            margin-top: 10px;
            
            .selected-tags {
                display: flex;
                flex-wrap: wrap;
                gap: 10px;
                margin-top: 10px;
            }

            .tag-item {
                display: flex;
                align-items: center;
                padding: 5px 10px;
                border-radius: 20px;
                color: #fff;
                font-weight: bold;
            }

            .close-icon {
                margin-left: 10px;
                cursor: pointer;
                font-size: 18px;
            }
        }
    }
}
</style>
